import { Grid } from '@mui/material';
import { useContext, useState } from 'react';
import CompanyLogo from './CompanyLogo';
import CompanyForm from './CompanyForm';
import CompanyAddress from './CompanyAddress';
import CurrentUserContext from '@/Contexts/CurrentUser';
import { CompanyTypeEnum, UpdateCompanyDocument } from '@/graphql/generated/graphql';
import { useForm } from 'react-hook-form';
import type { CompanyFormValues } from './types';
import NotifyAboutChanges from '../../NotifyAboutChanges';
import { useMutation } from '@apollo/client/react';
import { displayNotification } from '@/utils/notifications';

const EditCompanyDetails: React.FC = () => {
  const { currentCompany } = useContext(CurrentUserContext);
  const [updateCompany] = useMutation(UpdateCompanyDocument);
  const [pendingLogoPreviewUrl, setPendingLogoPreviewUrl] = useState<string | null>(null);
  const [pendingLogoSignedId, setPendingLogoSignedId] = useState<string | null>(null);

  const {
    handleSubmit,
    reset: resetCompany,
    formState: { errors, isDirty },
    control,
    getValues,
    setValue,
  } = useForm<CompanyFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: currentCompany?.name || '',
      tradingName: currentCompany?.tradingName || '',
      vatNumber: currentCompany?.vatNumber || '',
      companyType: currentCompany?.companyType || CompanyTypeEnum.Ltd,
      companyNumber: currentCompany?.companyNumber || '',
      primaryPhone: currentCompany?.primaryPhone || '',
      primaryEmail: currentCompany?.primaryEmail || '',
      street: currentCompany?.address?.street || '',
      city: currentCompany?.address?.city || '',
      state: currentCompany?.address?.state || '',
      zip: currentCompany?.address?.zip || '',
      country: 'United Kingdom',
      logoSignedId: '',
    },
  });

  const handleCompanyUpdate = (data: CompanyFormValues) => {
    updateCompany({
      variables: {
        id: currentCompany?.id as string,
        ...data,
      },
      onCompleted: () => {
        displayNotification({
          type: 'success',
          text: 'Company details updated successfully',
        });
      },
      onError: () =>
        displayNotification({
          type: 'error',
          text: 'Unable to update the company details, try again later.',
        }),
    });
  };

  if (!currentCompany) return null;

  return (
    <Grid container spacing={2} sx={{ width: '100%', padding: 2 }}>
      <Grid size={2}>
        <CompanyLogo
          pendingLogoPreviewUrl={pendingLogoPreviewUrl}
          setPendingLogoPreviewUrl={setPendingLogoPreviewUrl}
          pendingLogoSignedId={pendingLogoSignedId}
          setPendingLogoSignedId={setPendingLogoSignedId}
          onImageChange={(id) => setValue('logoSignedId', id, { shouldDirty: true })}
        />
      </Grid>
      <Grid size={5}>
        <CompanyForm control={control} formErrors={errors} getValues={getValues} />
      </Grid>
      <Grid size={5}>
        <CompanyAddress control={control} formErrors={errors} getValues={getValues} />
      </Grid>
      <NotifyAboutChanges
        hasChanged={isDirty}
        handleReset={() => {
          resetCompany();
          setPendingLogoPreviewUrl(null);
          setPendingLogoSignedId(null);
        }}
        handleSave={handleSubmit(handleCompanyUpdate)}
      />
    </Grid>
  );
};

export default EditCompanyDetails;
